import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, List, Link } from "@mui/material";
import { useAppSelector } from "../../store/hooks";
import { useGetPatientPlanQuery } from "../../pages/patient-dashboard/services/GetPatientPlanAPISlice";
import { Trimester } from "../../utility/interfaces/carePlanInterfaces";
import CustomDropdown from "../../components/custom-dropdown/CustomDropdown";
import ActivityListItem from "./ActivityListItem";
import ActivityHeader from "./ActivityHeader";
import { useTranslation } from "react-i18next";
import styles from "./PointsEarnedSection.module.scss";
import PointsEarnedSectionSkeleton from "./PointsEarnedSectionSkeleton";
import DataLoader from "../../components/custom-data-loader/DataLoader";

const MAX_VISIBLE_ACTIVITIES = 15;

const PointsEarnedSection: React.FC = () => {
  const {
    patientProfileInfo: {
      patientProfileId,
      preferredLanguage: patientPreferredLang,
    },
  } = useAppSelector((state) => state.patientProfileDetails);

  const { data, isLoading, error } = useGetPatientPlanQuery({
    patientId: patientProfileId,
    patientPreferredLang,
  });

  const { t } = useTranslation();
  const [selectedTrimesterNumber, setSelectedTrimesterNumber] = useState<
    number | null
  >(null);
  const [showAll, setShowAll] = useState(false);

  const filteredTrimesters = useMemo(() => {
    const allTris = data?.patientPlansInfo ?? [];
    return selectedTrimesterNumber === -1
      ? allTris
      : allTris.filter(
          (t) =>
            t.trimesterDisplayName?.trim() !== "" &&
            t.trimesterPlans?.length > 0
        );
  }, [data, selectedTrimesterNumber]);

  const selectedTrimester = useMemo(() => {
    if (selectedTrimesterNumber === -1) return null;
    return (
      filteredTrimesters.find(
        (t) => t.trimesterNumber === selectedTrimesterNumber
      ) || null
    );
  }, [filteredTrimesters, selectedTrimesterNumber]);

  useEffect(() => {
    if (selectedTrimesterNumber === null && filteredTrimesters.length > 0) {
      const current = filteredTrimesters.find((t) => t.trimesterCurrentStatus);
      setSelectedTrimesterNumber(
        current?.trimesterNumber ?? filteredTrimesters[0].trimesterNumber
      );
    }
  }, [filteredTrimesters, selectedTrimesterNumber]);

  const allActivities = useMemo(() => {
    const extractCompleted = (plans: Trimester["trimesterPlans"]) =>
      plans.flatMap((plan) =>
        plan.activities.filter(
          (a) => a.activityCompletionStatusCode === "Completed"
        )
      );

    const sourceTris =
      selectedTrimesterNumber === -1
        ? filteredTrimesters
        : selectedTrimester
        ? [selectedTrimester]
        : [];

    const activities = sourceTris.flatMap((t) =>
      extractCompleted(t.trimesterPlans)
    );

    return activities.sort(
      (a, b) =>
        new Date(b.updatedDateTime).getTime() -
        new Date(a.updatedDateTime).getTime()
    );
  }, [selectedTrimesterNumber, filteredTrimesters, selectedTrimester]);

  const totalPoints = useMemo(
    () =>
      allActivities.reduce((acc, curr) => acc + (curr.activityPoints ?? 0), 0),
    [allActivities]
  );

  const visibleActivities = showAll
    ? allActivities
    : allActivities.slice(0, MAX_VISIBLE_ACTIVITIES);

  const trimesterOptions = useMemo(() => {
    const nonEmpty =
      data?.patientPlansInfo.filter(
        (t) =>
          t.trimesterDisplayName?.trim() !== "" && t.trimesterPlans?.length > 0
      ) ?? [];

    const baseOptions = nonEmpty.map((t) => ({
      label: t.trimesterDisplayName!,
      value: t.trimesterNumber,
    }));

    return [{ label: t("myRewardsPage.all"), value: -1 }, ...baseOptions];
  }, [data, t]);

  return (
    <DataLoader
      isLoading={isLoading}
      error={error}
      data={data}
      loadingComponent={<PointsEarnedSectionSkeleton />}
      emptyComponent={<Typography>{t("noData")}</Typography>}
      errorComponent={
        <Typography color="error">
          {t("myRewardsPage.errorLoadingData")}
        </Typography>
      }
    >
      {() => (
        <Box className={styles.sectionWrapper}>
          <Box className={styles.header}>
            <Typography variant="h3">
              {t("myRewardsPage.pointsEarned")}
            </Typography>
            <CustomDropdown
              value={selectedTrimesterNumber ?? -1}
              options={trimesterOptions}
              placeholder={t("select")}
              changeTrigger={(value) => {
                setSelectedTrimesterNumber(value as number);
                setShowAll(false);
              }}
            />
          </Box>

          {visibleActivities.length === 0 ? (
            <Typography variant="body2" color="text.secondary" mt={2}>
              {t("myRewardsPage.noActivities")}
            </Typography>
          ) : (
            <List
              disablePadding
              subheader={<ActivityHeader totalPoints={totalPoints} />}
            >
              {visibleActivities.map((activity, index) => (
                <ActivityListItem key={index} activity={activity} />
              ))}
            </List>
          )}

          {allActivities.length > MAX_VISIBLE_ACTIVITIES && !showAll && (
            <Box mt={2} textAlign="center">
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  setShowAll(true);
                }}
              >
                {t("myRewardsPage.showMore")}
              </Link>
            </Box>
          )}
        </Box>
      )}
    </DataLoader>
  );
};

export default PointsEarnedSection;
